/**
 * @voltxampere/web — Preset selector orchestration (Sprint 17 →
 * Sprint 18 Paket B B8).
 *
 * Sprint 17 modal preset-dialog'undan kalan API. Modal Sprint 18
 * Paket B'de page-level <vxa-pane-presets> ile değiştirildi
 * (B4-B5+B8). Bu modülün geri kalanı:
 *
 *   - `selectedCategory` — DEVRELER sayfasında aktif kategori
 *   - `selectCategory(cat)` — kategoriyi değiştir
 *   - `loadPreset(id)` — preset'i circuit'e yükle (Cmd+Z reverts)
 *
 * Modal-state signal'leri (presetDialogOpen, selectedPresetId,
 * openPresetDialog, closePresetDialog, selectPreset) B8'de silindi
 * — page-level catalog filter chips + click-to-load akışı bu API'yi
 * gereksiz kıldı.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import { findPresetById, type Preset, type PresetCategory } from "@voltxampere/presets";
import type { Circuit } from "@voltxampere/schema";
import { LoadCircuitCommand } from "@voltxampere/ui/canvas";
import { currentCircuit } from "./circuit.js";
import { selectedNodes } from "./canvas.js";
import { bindProbe, unbindProbe } from "./channels.js";
import { undoStack } from "./undo-stack.js";
import { gateCircuitForEngine } from "./validation-gate.js";

// Sprint 25 R5 R5-IA-2 — DEVRELER pane choice fan-out reduction
// (F-IA-13). Pre-fix default `null` showed all 17 preset cards on
// boot, pushing the pane's top-level interactive count to 31 (>25
// Hick's law). Default `"basic"` (Sprint 25 R5) reduced the
// initial visible card count from 17 to 8 but Sprint 80.Y Phase 1
// audit confirmed Şefim's #5/#6 observation ("preset'ler kopuk /
// kafasına göre") was driven by this narrow default — users
// landed on 8 cards and didn't realise 23 more existed in the
// other chip filters.
//
// Sprint 80.Y L-D-1 changes the default to null so the DEVRELER
// pane renders via renderAccordion (vxa-pane-presets.ts:466)
// rather than renderFlatGrid. Combined with the
// _expandedCategories default = ALL CATEGORIES, all 31 presets
// are visible at first paint in their category sections. The
// chip filters still narrow on demand; Sprint 26's "land on
// focused 3-4 cards" intent is preserved by per-section
// collapsible accordion headers (user collapses what they
// don't need).
export const selectedCategory = signal<PresetCategory | null>(null);

export function selectCategory(cat: PresetCategory | null): void {
  selectedCategory.value = cat;
}

/**
 * Compute a fit-to-screen viewport for a Sprint 25 R2 lane 4D-A
 * hand-authored preset. Walks every component's body center and
 * every wire path point, takes the bbox, and centers the viewport
 * on it with a `zoom` chosen so the longer side fits the
 * `targetSize` viewport span. Falls back to the schema-default
 * viewport (`center: 0,0, zoom: 1`) when the preset has no
 * components yet.
 *
 * The chosen geometry mirrors v1's `fitToScreen` (`~/Desktop/
 * VoltXAmpere/src/io/spice-layout.js`) without porting the full
 * BFS auto-layout — Lane 4E will revisit this and make it
 * algorithm-driven.
 */
function computeFitViewport(circuit: Circuit, targetSize = 800): Circuit["viewport"] {
  if (circuit.components.length === 0) {
    return { center: { x: 0, y: 0 }, zoom: 1, rotation: 0 };
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const comp of circuit.components) {
    if (comp.position.x < minX) minX = comp.position.x;
    if (comp.position.y < minY) minY = comp.position.y;
    if (comp.position.x > maxX) maxX = comp.position.x;
    if (comp.position.y > maxY) maxY = comp.position.y;
    for (const term of comp.terminals) {
      if (term.position.x < minX) minX = term.position.x;
      if (term.position.y < minY) minY = term.position.y;
      if (term.position.x > maxX) maxX = term.position.x;
      if (term.position.y > maxY) maxY = term.position.y;
    }
  }
  for (const wire of circuit.wires) {
    for (const p of wire.path) {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    }
  }
  // Margin so endpoints don't kiss the canvas edge.
  const margin = 60;
  const widthW = Math.max(maxX - minX + margin * 2, 1);
  const heightW = Math.max(maxY - minY + margin * 2, 1);
  const longest = Math.max(widthW, heightW);
  const zoom = Math.min(2, Math.max(0.25, targetSize / longest));
  return {
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
    zoom,
    rotation: 0,
  };
}

/**
 * Apply a preset by id. Returns `true` when the load was
 * accepted (the preset exists, the circuit was swapped). Failed
 * lookups silently noop and return `false`.
 *
 * Sprint 25 R2 lane 4D-A:
 *   - Hand-authored layouts now ship a `viewport` field via
 *     `computeFitViewport` so the canvas centers on the new
 *     circuit instead of inheriting the previous one.
 *   - Loaded circuit is fed to `gateCircuitForEngine` so the user
 *     sees a toast immediately if the preset has stale terminals
 *     (legacy presets that have not been migrated to layout-aware
 *     form yet, or future presets with topology bugs).
 */
export function loadPreset(id: string): boolean {
  const preset: Preset | undefined = findPresetById(id);
  if (preset === undefined) return false;
  const baseline = currentCircuit.value ?? preset.circuit;
  // Inject a fit-to-screen viewport unless the preset already
  // ships one (preset author-explicit overrides win).
  const presetCircuit: Circuit =
    preset.circuit.viewport === undefined
      ? { ...preset.circuit, viewport: computeFitViewport(preset.circuit) }
      : preset.circuit;
  const cmd = new LoadCircuitCommand(presetCircuit, `Preset: ${preset.name.tr}`);
  currentCircuit.value = undoStack.execute(cmd, baseline);
  // Sprint 28 K3 — preset load = guided onboarding. R₁ pre-select
  // didaktik akışı destekler. Sprint 25 R5 R5-VIS-3 (F-V-03) drove
  // selection to empty here to clear "Karışık seçim — ortak parametre
  // yok" stale state from the previous circuit; Sprint 28 keeps the
  // stale-state cure (the selection is rewritten, not merged) but
  // replaces empty with a default target so Inspector lands on a
  // didactic component instead of the empty CTA.
  //
  // SelectTool Escape pattern (Sprint 25 R5 R5-CR-2) sadece toolbar
  // reset için geçerli — preset path mockup canonical R₁ pre-select
  // kuralına uyar (zenit-platform 2.html line 2070-2135 hero fixture
  // V₁/R₁/R₂/GND topology with R₁ pre-selected and parameter slider
  // visible).
  const defaultTargetId = pickDefaultInspectorTarget(currentCircuit.value);
  selectedNodes.value = defaultTargetId !== null ? [defaultTargetId] : [];
  // Sprint 25 R5 R5-VIS-2 — orphan probe stray cleanup on preset
  // load (F-V-02, 16/16 preset PNGs in UX_QUALITY_AUDIT). Sprint 25
  // R3-4 added auto-bind on the BOOT path (main.ts:461 calls
  // pickInitialProbeBinding); the preset path was the gap, so probe
  // bindings from the previous circuit's nets remained pinned to
  // ids that no longer exist in the new circuit's net table — the
  // canvas painted "—" stray dashes at orphan world coords.
  //
  // Strategy mirrors the boot path: drop every CH probe binding,
  // then auto-bind CH1 to the new circuit's signal-net (preferred
  // by name "mid", else first non-ground). Math channels (M1/M2)
  // are not probe-bindable so they stay untouched.
  unbindProbe("ch1");
  unbindProbe("ch2");
  unbindProbe("ch3");
  unbindProbe("ch4");
  const initialProbeNet = pickInitialProbeNet(currentCircuit.value);
  if (initialProbeNet !== null) bindProbe("ch1", initialProbeNet);
  // Run the Sprint 25 R2 lane 2 validation gate. Errors surface as
  // toast (warnings: true so the disconnect / dangling-pin warnings
  // also reach the user). This intentionally happens AFTER the
  // circuit lands so undo + reload still work even on invalid
  // presets — the gate is informational here, not blocking.
  gateCircuitForEngine(currentCircuit.value, { warnings: true });
  return true;
}

/**
 * Sprint 25 R5 R5-VIS-2 — preset-path probe auto-bind helper.
 *
 * Mirrors `pickInitialProbeBinding` in `main.ts` (Sprint 25 R3-4
 * boot helper). Picks a net to attach CH1 to: prefer a non-ground
 * net named "mid" (canonical voltage-divider midpoint), else the
 * first non-ground net the circuit declares. Returns null when
 * the circuit has no signal nets, which means CH1 stays unbound
 * and the empty-state CTA branch in `<vxa-waveform>` kicks in.
 *
 * Inline duplication is intentional for now: importing main.ts
 * from a store module would create a circular dependency. A
 * future sprint may consolidate this into `store/probes.ts`.
 */
function pickInitialProbeNet(circuit: Circuit | null): string | null {
  if (circuit === null) return null;
  const byName = circuit.nets.find((n) => n.name === "mid" && !n.isGround);
  if (byName) return byName.id;
  const firstSignal = circuit.nets.find((n) => !n.isGround);
  return firstSignal?.id ?? null;
}

/**
 * Sprint 28 K3 — preset-path Inspector default selection helper.
 *
 * Picks the most didactically useful component to pre-select on
 * preset load. Heuristic order:
 *
 *   1. First resistor (R₁ or first `type === "resistor"`) — the
 *      mockup canonical anchor (zenit-platform 2.html voltage-divider
 *      hero shows R₁ pre-selected with its parameter slider open).
 *   2. First non-source / non-ground component — keeps Inspector
 *      pointing at a parameterised passive (capacitor, inductor)
 *      when the preset has no resistors (e.g. LC tank).
 *   3. First component as a last resort — even sources are better
 *      than empty Inspector for a guided onboarding moment.
 *
 * Returns null only when the circuit has zero components (defensive
 * — `loadPreset` callers already guard against that, but the helper
 * stays graceful so tests can call it on edge fixtures).
 *
 * Inline next to `pickInitialProbeNet` for the same circular-import
 * reason: a future `store/inspector-defaults.ts` consolidation may
 * lift both helpers up.
 */
function pickDefaultInspectorTarget(circuit: Circuit | null): string | null {
  if (circuit === null || circuit.components.length === 0) return null;
  const firstResistor = circuit.components.find((c) => c.type === "resistor");
  if (firstResistor) return firstResistor.id;
  const sourceTypes = new Set([
    "voltage-source",
    "current-source",
    "ac-source",
    "pulse-source",
    "pwl-source",
    "ground",
  ]);
  const nonSource = circuit.components.find((c) => !sourceTypes.has(c.type));
  if (nonSource) return nonSource.id;
  return circuit.components[0]?.id ?? null;
}
